import React, { useEffect, useRef, useState } from "react";
import { COUNTRIES } from "../../data/content";

/**
 * Compact country picker button + popover.
 * Used in link-in-bio / gift pages to let the user override the
 * auto-detected country.
 */
export default function CountryPicker({
  country,
  onChange,
  align = "left",
  compact = false,
  testId = "country-picker",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = COUNTRIES.find((c) => c.code === country);

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 border border-stone-900/25 text-[11px] tracking-[0.22em] uppercase hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-200 ${
          compact ? "justify-center min-w-10" : ""
        }`}
        aria-label={current ? `Shopping from ${current.label}` : "Choose shopping country"}
        data-testid={`${testId}-button`}
      >
        <span className="text-base leading-none">{current?.flag || "🌐"}</span>
        <span className={compact ? "hidden md:inline" : ""}>
          {current ? current.label : "Choose country"}
        </span>
        <span className={compact ? "hidden md:inline opacity-60" : "opacity-60"}>▾</span>
      </button>

      <div
        className={`absolute z-40 ${
          align === "right" ? "right-0" : "left-0"
        } top-full mt-2 min-w-[240px] bg-[color:var(--paper)] border border-stone-900/15 shadow-[0_20px_60px_rgba(28,25,23,0.15)] transition-all duration-200 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        role="menu"
        data-testid={`${testId}-menu`}
      >
        <div className="px-4 pt-3 pb-2 text-[10px] tracking-[0.28em] uppercase text-stone-500 font-medium">
          Shopping from
        </div>
        <ul
          data-lenis-prevent
          className="max-h-[min(65vh,540px)] overflow-y-auto overscroll-contain pb-2"
        >
          {COUNTRIES.map((c) => {
            const active = c.code === country;
            return (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm flex items-center justify-between gap-3 transition-colors duration-150 ${
                    active
                      ? "bg-stone-900 text-[color:var(--paper)]"
                      : "hover:bg-stone-900/5"
                  }`}
                  data-testid={`${testId}-${c.code}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span>{c.label}</span>
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase opacity-70">
                    {c.store}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
